import type { BookingConfirmationSummary } from '@massilia/queries'

import { ArrowRight, CheckCircle, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { InsuranceUpsellPanel } from './insurance-upsell-panel'

const INSURANCE_LABEL = 'Assurance voyage'

const UPSELL_ITEMS = [
  { label: 'Sélection de sièges supplémentaires' },
  { label: 'Location de voiture' },
  { label: 'Réservation de siège' },
  { label: INSURANCE_LABEL },
  { label: 'Réserver un hôtel ou logement' },
  { label: 'Ajouter des bagages supplémentaires' },
  { label: 'Choix de repas à bord' },
  { label: 'Embarquement prioritaire et livraison bagages rapide' },
] as const

interface CompleteYourTripSectionProps {
  bookingId?: string
  sessionId?: string
  hasInsurance?: boolean
  summary?: BookingConfirmationSummary
}

export function CompleteYourTripSection({
  bookingId,
  sessionId,
  hasInsurance,
  summary,
}: CompleteYourTripSectionProps) {
  const [isInsuranceOpen, setIsInsuranceOpen] = useState(false)

  const isInsuranceAvailable = (!!bookingId || !!sessionId) && !hasInsurance

  return (
    <div className="flex flex-col gap-7 rounded-2xl border border-gray-light-200 bg-white p-6">
      <div className="text-lg leading-7 font-semibold text-gray-light-900 sm:text-2xl">
        Compléter votre voyage !
      </div>

      <div className="flex flex-col gap-3">
        {UPSELL_ITEMS.map((item, i) => {
          const isInsurance = item.label === INSURANCE_LABEL

          return (
            <div key={i}>
              <div
                className={`flex items-center justify-between pb-4 ${
                  i < UPSELL_ITEMS.length - 1 ? 'border-b border-gray-light-200' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`size-4 shrink-0 ${
                      isInsurance && isInsuranceAvailable ? 'bg-brand-700' : 'bg-gray-light-200'
                    }`}
                  />
                  <span
                    className={`text-sm leading-5 font-semibold sm:text-base ${
                      isInsurance && isInsuranceAvailable ? 'text-brand-700' : 'text-gray-light-900'
                    }`}
                  >
                    {item.label}
                  </span>
                </div>

                {renderItemIndicator({
                  bookingId,
                  hasInsurance,
                  isInsurance,
                  isInsuranceAvailable,
                  isInsuranceOpen,
                  onToggle: () => setIsInsuranceOpen((prev) => !prev),
                })}
              </div>

              {isInsurance && isInsuranceAvailable && isInsuranceOpen && (
                <div className="animate-[fadeSlideIn_150ms_ease-out] pb-4">
                  <InsuranceUpsellPanel
                    bookingId={bookingId}
                    sessionId={sessionId}
                    summary={summary}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function renderItemIndicator({
  bookingId,
  hasInsurance,
  isInsurance,
  isInsuranceAvailable,
  isInsuranceOpen,
  onToggle,
}: {
  bookingId?: string
  hasInsurance?: boolean
  isInsurance: boolean
  isInsuranceAvailable: boolean
  isInsuranceOpen: boolean
  onToggle: () => void
}) {
  if (isInsurance && hasInsurance) {
    return (
      <span className="flex items-center gap-1 rounded-full bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700">
        <CheckCircle className="size-3" />
        Souscrite
      </span>
    )
  }

  if (isInsurance && isInsuranceAvailable) {
    return (
      <button className="flex items-center gap-1" onClick={onToggle} type="button">
        <ChevronDown
          className={`size-6 shrink-0 text-gray-light-600 transition-transform duration-200 ${
            isInsuranceOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
    )
  }

  if (!isInsurance || !bookingId) {
    return (
      <span className="rounded-full bg-gray-light-200 px-2 py-1 text-xs font-semibold text-gray-light-900">
        Bientôt disponible
      </span>
    )
  }

  return <ArrowRight className="size-6 shrink-0 text-gray-light-600" />
}
